import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Navbar from '../components/Navbar';
import QuizComponent from '../components/QuizComponent';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, ArrowRight, Clock, CheckCircle2, Circle,
  Download, BookOpen, Loader2, Lock, CreditCard, Award
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainingLesson = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchLesson();
    // Scroll to top when lesson changes
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchLesson = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/training/lessons/${slug}`, { headers });
      setLesson(response.data);
    } catch (error) {
      console.error('Failed to fetch lesson:', error);
      if (error.response?.status === 404) {
        navigate('/training');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleComplete = async () => {
    if (!user) {
      toast.error('Please log in to track your progress');
      navigate('/login');
      return;
    }

    setCompleting(true);
    try {
      if (lesson.completed) {
        await axios.delete(`${API}/training/lessons/${lesson.id}/complete`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLesson({ ...lesson, completed: false });
        toast.success('Progress unmarked');
      } else {
        await axios.post(`${API}/training/lessons/${lesson.id}/complete`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLesson({ ...lesson, completed: true });
        toast.success('Lesson marked as complete! 🎉');
      }
    } catch (error) {
      toast.error('Failed to update progress');
    } finally {
      setCompleting(false);
    }
  };

  const navigateBackToCourse = () => {
    const courseId = lesson?.course?.id;
    const courseSlug = lesson?.course?.slug;
    // If course has a regular course ID format (UUID), go to /courses/{id}
    if (courseId && courseId.includes('-')) {
      navigate(`/courses/${courseId}`);
    } else if (courseSlug) {
      navigate(`/training/${courseSlug}`);
    } else {
      navigate('/training');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!lesson) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="pt-20">
        {/* Top Bar */}
        <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={navigateBackToCourse}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Back to Course</span>
              </Button>
              <div className="hidden md:block">
                <p className="text-sm text-slate-500">{lesson.course?.title}</p>
                <p className="font-medium text-slate-900">{lesson.title}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Badge variant="outline" className="hidden sm:flex">
                <Clock className="w-3 h-3 mr-1" />
                {lesson.duration_minutes} min
              </Badge>
              
              <Button
                variant={lesson.completed ? 'outline' : 'default'}
                size="sm"
                onClick={toggleComplete}
                disabled={completing}
              >
                {completing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : lesson.completed ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                    Completed
                  </>
                ) : (
                  <>
                    <Circle className="w-4 h-4 mr-2" />
                    Mark Complete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Lesson Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Badge className="bg-primary/10 text-primary">{lesson.tool_name}</Badge>
              <Badge variant="outline">{lesson.level}</Badge>
              <Badge variant="outline">Lesson {lesson.order_index}</Badge>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              {lesson.title}
            </h1>
            <p className="text-lg text-slate-600">
              {lesson.short_summary}
            </p>
          </div>

          {/* Downloads */}
          {lesson.downloads && lesson.downloads.length > 0 && (
            <Card className="border-slate-200 mb-8 bg-primary/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Download className="w-5 h-5 text-primary" />
                  <span className="font-medium text-slate-900">Downloads & Resources</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {lesson.downloads.map((download, i) => (
                    <a 
                      key={i}
                      href={`${API}/downloads/${encodeURIComponent(download.name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      <Button variant="outline" size="sm" className="bg-white">
                        <Download className="w-4 h-4 mr-2" />
                        {download.name}
                      </Button>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lesson Content */}
          <Card className="border-slate-200">
            <CardContent className="p-6 sm:p-8">
              <article className="prose prose-slate max-w-none prose-headings:font-heading prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-a:text-primary prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-table:text-sm">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom rendering for code blocks
                    code: ({ node, inline, className, children, ...props }) => {
                      if (inline) {
                        return (
                          <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                            {children}
                          </code>
                        );
                      }
                      return (
                        <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                          <code className="text-sm font-mono" {...props}>
                            {children}
                          </code>
                        </pre>
                      );
                    },
                    // Custom table styling
                    table: ({ children }) => (
                      <div className="overflow-x-auto">
                        <table className="min-w-full border-collapse">{children}</table>
                      </div>
                    ),
                    th: ({ children }) => (
                      <th className="border border-slate-200 bg-slate-50 px-4 py-2 text-left font-semibold">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="border border-slate-200 px-4 py-2">{children}</td>
                    ),
                    // Links open in new tab
                    a: ({ href, children }) => (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        {children}
                      </a>
                    ),
                    // Filter out Quick Quiz section - we render it separately
                    h1: ({ children, ...props }) => {
                      const text = String(children);
                      if (text.toLowerCase().includes('quick quiz')) {
                        return null;
                      }
                      return <h1 {...props}>{children}</h1>;
                    }
                  }}
                >
                  {lesson.content_md?.split(/# Quick Quiz/i)[0] || lesson.content_md}
                </ReactMarkdown>
              </article>
            </CardContent>
          </Card>

          {/* Quiz Section */}
          {lesson.quiz && (
            <div className="mt-8">
              <QuizComponent 
                quizId={lesson.quiz.id} 
                lessonTitle={lesson.title}
                onComplete={(result) => {
                  if (result.passed) {
                    setLesson({ ...lesson, quiz_passed: true });
                  }
                }}
              />
            </div>
          )}

          {/* Completion Status Banner */}
          {(lesson.completed || lesson.quiz_passed) && (
            <Card className="mt-8 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-center gap-6">
                  {lesson.completed && (
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Lesson Complete</span>
                    </div>
                  )}
                  {lesson.quiz_passed && (
                    <div className="flex items-center gap-2 text-emerald-700">
                      <Award className="w-5 h-5" />
                      <span className="font-medium">Quiz Passed</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between">
            {lesson.prev_lesson ? (
              <Button 
                variant="outline"
                onClick={() => navigate(`/training/lesson/${lesson.prev_lesson.slug}`)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous: {lesson.prev_lesson.title.substring(0, 20)}...
              </Button>
            ) : (
              <div />
            )}

            {lesson.next_lesson ? (
              <Button 
                onClick={() => navigate(`/training/lesson/${lesson.next_lesson.slug}`)}
              >
                Next: {lesson.next_lesson.title.substring(0, 20)}...
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={navigateBackToCourse}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Complete Course
              </Button>
            )}
          </div>

          {/* Mark Complete CTA */}
          {!lesson.completed && (
            <Card className="mt-8 border-green-200 bg-green-50">
              <CardContent className="p-6 text-center">
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  Finished this lesson?
                </h3>
                <p className="text-slate-600 mb-4">
                  Mark it complete to track your progress and unlock your certificate.
                </p>
                <Button 
                  onClick={toggleComplete}
                  disabled={completing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {completing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Mark as Complete
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrainingLesson;
